import { useState, useEffect } from 'react';

interface CookieConsent {
  necessary: boolean;
  analytics: boolean;
  timestamp: number;
}

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setShowBanner(true);
    } else {
      const consentData: CookieConsent = JSON.parse(consent);
      if (consentData.analytics) {
        loadExternalResources();
      }
    }
  }, []);

  const loadExternalResources = () => {
    // Google Fonts laden
    if (!document.getElementById('google-fonts-preconnect')) {
      const preconnect1 = document.createElement('link');
      preconnect1.id = 'google-fonts-preconnect';
      preconnect1.rel = 'preconnect';
      preconnect1.href = 'https://fonts.googleapis.com';
      document.head.appendChild(preconnect1);

      const preconnect2 = document.createElement('link');
      preconnect2.id = 'google-fonts-preconnect-gstatic';
      preconnect2.rel = 'preconnect';
      preconnect2.href = 'https://fonts.gstatic.com';
      preconnect2.crossOrigin = 'anonymous';
      document.head.appendChild(preconnect2);

      const fontLink = document.createElement('link');
      fontLink.id = 'google-fonts-link';
      fontLink.rel = 'stylesheet';
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Pacifico&display=swap';
      document.head.appendChild(fontLink);
    }

    // Font Awesome laden
    if (!document.getElementById('font-awesome-link')) {
      const fontAwesome = document.createElement('link');
      fontAwesome.id = 'font-awesome-link';
      fontAwesome.rel = 'stylesheet';
      fontAwesome.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
      document.head.appendChild(fontAwesome);
    }

    // Remix Icon wird lokal gebündelt (siehe main.tsx) – nicht mehr per CDN nachladen

    // Google Analytics laden
    loadGoogleAnalytics();
  };

  const loadGoogleAnalytics = () => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted'
      });
    }
  };

  const removeExternalResources = () => {
    // Google Fonts entfernen
    const googleFontsPreconnect = document.getElementById('google-fonts-preconnect');
    const googleFontsPreconnectGstatic = document.getElementById('google-fonts-preconnect-gstatic');
    const googleFontsLink = document.getElementById('google-fonts-link');
    if (googleFontsPreconnect) googleFontsPreconnect.remove();
    if (googleFontsPreconnectGstatic) googleFontsPreconnectGstatic.remove();
    if (googleFontsLink) googleFontsLink.remove();

    // Font Awesome entfernen
    const fontAwesome = document.getElementById('font-awesome-link');
    if (fontAwesome) fontAwesome.remove();

    // Remix Icon wird lokal gebündelt (siehe main.tsx) – nichts zu entfernen

    // Google Analytics entfernen
    removeGoogleAnalytics();
  };

  const removeGoogleAnalytics = () => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied',
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied'
      });
    }

    // Cookies löschen
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const cookieName = cookie.split('=')[0].trim();
      if (cookieName.startsWith('_ga') || cookieName.startsWith('_gid') || cookieName.startsWith('_gat')) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      }
    }
  };

  const handleAcceptAll = () => {
    const consent: CookieConsent = {
      necessary: true,
      analytics: true,
      timestamp: Date.now()
    };
    localStorage.setItem('cookie-consent', JSON.stringify(consent));
    loadExternalResources();
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const consent: CookieConsent = {
      necessary: true,
      analytics: false,
      timestamp: Date.now()
    };
    localStorage.setItem('cookie-consent', JSON.stringify(consent));
    removeExternalResources();
    setShowBanner(false);
  };

  const handleSaveSettings = () => {
    const consent: CookieConsent = {
      necessary: true,
      analytics: analyticsEnabled,
      timestamp: Date.now()
    };
    localStorage.setItem('cookie-consent', JSON.stringify(consent));
    
    if (analyticsEnabled) {
      loadExternalResources();
    } else {
      removeExternalResources();
    }
    
    setShowSettings(false);
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-[9998] backdrop-blur-sm"></div>

      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-white border-t-2 border-gray-200 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
          {!showSettings ? (
            // Hauptansicht
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-6">
              <div className="flex-1">
                <div className="flex items-start gap-3 mb-3">
                  <i className="ri-cookie-line text-3xl text-[#5DD9C1] flex-shrink-0 mt-1"></i>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      Cookies? Ja, aber transparent
                    </h3>
                    <p className="text-sm md:text-base text-[#6B6B6B] leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Wir nutzen Cookies für die Funktion der Website (notwendig) und externe Dienste wie Google Fonts, Font-Icons sowie Google Analytics (optional), um zu verstehen, wie du die Seite nutzt. Du entscheidest, was okay ist.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSettings(true)}
                  className="text-sm text-[#3FBFA7] hover:underline font-medium flex items-center gap-1 cursor-pointer"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  <i className="ri-settings-3-line"></i>
                  Einstellungen anpassen
                </button>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <button
                  onClick={handleRejectAll}
                  className="px-6 py-3 bg-white border-2 border-gray-300 text-[#1A1A1A] font-semibold rounded-lg hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Nur notwendige
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-3 bg-gradient-to-r from-[#5DD9C1] to-[#3FBFA7] text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Alle akzeptieren
                </button>
              </div>
            </div>
          ) : (
            // Einstellungsansicht
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl md:text-2xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Cookie-Einstellungen
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-500 hover:text-[#1A1A1A] cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Notwendige Cookies */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-shield-check-line text-xl text-[#5DD9C1]"></i>
                        <h4 className="font-bold text-[#1A1A1A]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          Notwendige Cookies
                        </h4>
                      </div>
                      <p className="text-sm text-[#6B6B6B]" style={{ fontFamily: "'Inter', sans-serif" }}>
                        Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
                      </p>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-[#6B6B6B] bg-gray-200 px-3 py-1 rounded-full">
                        Immer aktiv
                      </span>
                    </div>
                  </div>
                </div>

                {/* Analyse Cookies */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-bar-chart-line text-xl text-[#5DD9C1]"></i>
                        <h4 className="font-bold text-[#1A1A1A]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          Externe Dienste & Analyse
                        </h4>
                      </div>
                      <p className="text-sm text-[#6B6B6B] mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                        Diese Kategorie umfasst Google Fonts (Schriftarten), Font-Icons (Font Awesome, Remix Icon) und Google Analytics. Diese Dienste helfen uns, die Website schön darzustellen und zu verstehen, wie Besucher sie nutzen.
                      </p>
                      <a
                        href="/datenschutz"
                        className="text-sm text-[#3FBFA7] hover:underline inline-flex items-center gap-1"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        Mehr erfahren
                        <i className="ri-arrow-right-line"></i>
                      </a>
                    </div>
                    <div className="flex items-center">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={analyticsEnabled}
                          onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#5DD9C1]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-[#5DD9C1] peer-checked:to-[#3FBFA7]"></div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-6 py-3 bg-white border-2 border-gray-300 text-[#1A1A1A] font-semibold rounded-lg hover:bg-gray-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSaveSettings}
                  className="px-6 py-3 bg-gradient-to-r from-[#5DD9C1] to-[#3FBFA7] text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Einstellungen speichern
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
